// ./component/HospitalChat.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronLeft } from "react-icons/fi";
import { IoIosSend } from "react-icons/io";
import createSocket from "../../Pages/socket/socket";
import "./HospitalChat.css";
import { AppContext } from "../../appContext";
import { EditProfileLoader, Loader } from "../common/loader";
import { LuCheckCheck } from "react-icons/lu";
import { LuCheck } from "react-icons/lu";
import { IoCheckmarkDoneOutline, IoCheckmarkOutline } from "react-icons/io5";

export default function HospitalChat({ data, consultationId, closeFunction }) {
  const myRole = "hospital";
  const { loggedInUser, fetchUserProfile } = useContext(AppContext);

  const socket = useRef(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [joined, setJoined] = useState(false);
  const [participants, setParticipants] = useState({ doctor: null, hospital: null });
  const listRef = useRef(null);
  const readQueueRef = useRef(new Set());
  const readTimerRef = useRef(null);

  // ---- PROFILE IDS ----
  const hospitalProfileId =
    loggedInUser?.data?.hospitalProfile?._id ||
    data?.hospitalId ||
    (typeof window !== "undefined" && window.__HOSPITAL_PROFILE_ID) ||
    null;

  const doctorProfileId =
    data?._id ||
    (typeof window !== "undefined" && window.__DOCTOR_PROFILE_ID) ||
    "REPLACE_WITH_DOCTOR_PROFILE_ID";

  useEffect(() => {
    if (!hospitalProfileId && typeof fetchUserProfile === "function") {
      fetchUserProfile();
    }
  }, [hospitalProfileId, fetchUserProfile]);

  // ---- PAYLOAD ----
  const payload = useMemo(
    () => ({ consultationId, hospitalId: hospitalProfileId, doctorId: doctorProfileId }),
    [consultationId, hospitalProfileId, doctorProfileId]
  );

  // ---- AUTO SCROLL ----
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  // ---- HELPERS ----
  const initials = (name = "") => {
    const p = name.trim().split(/\s+/).filter(Boolean);
    const a = p[0]?.[0] || "";
    the: {
      /* keep exactly as-is, only UI changed */
    }
    const b = p[1]?.[0] || p[0]?.[1] || "";
    return (a + b).toUpperCase() || "??";
  };

  const flushReadAcks = () => {
    const ids = Array.from(readQueueRef.current);
    if (!ids.length) return;
    readQueueRef.current.clear();
    socket.current.emit("markRead", { consultationId, ids });
  };

  const scheduleFlush = () => {
    if (readTimerRef.current) clearTimeout(readTimerRef.current);
    readTimerRef.current = setTimeout(flushReadAcks, 150);
  };

  const enqueueUnreadFromOther = (list) => {
    let added = false;
    for (const m of list) {
      const fromOther = m.user?.name && m.user.name !== myRole;
      const alreadyReadByMe = m.read?.[myRole] === true;
      if (fromOther && !alreadyReadByMe) {
        readQueueRef.current.add(String(m._id));
        added = true;
      }
    }
    if (added) scheduleFlush();
  };

  // ---- SOCKET LOGIC ----
  useEffect(() => {
    if (!consultationId || !hospitalProfileId) return;

    socket.current = createSocket(loggedInUser);
    const s = socket.current;

    const bootstrap = () => {
      s.emit("createConsultationRoom", payload);
      s.emit("joinConsultation", { consultationId });
      s.emit("getHistory", { consultationId, limit: 50 });
    };

    const onConnect = () => bootstrap();
    const onParticipants = ({ doctor, hospital }) => setParticipants({ doctor, hospital });
    const onJoined = () => setJoined(true);

    const onHistory = ({ messages: list = [] }) => {
      const seeded = list.map((m) => ({
        ...m,
        _clientRead: m.user?.name === myRole ? !!m.read?.doctor : false,
      }));
      setMessages(seeded);
      enqueueUnreadFromOther(list);
    };

    const onMessage = ({ message }) => {
      const isMe = message.user?.name === myRole;
      const enriched = { ...message, _clientRead: isMe ? !!message.read?.doctor : false };
      setMessages((prev) => [...prev, enriched]);
      if (!isMe) enqueueUnreadFromOther([message]);
    };

    const onUnreadUpdated = ({ unread }) => {
      if (typeof unread?.doctor === "number" && unread.doctor === 0) {
        setMessages((prev) =>
          prev.map((m) => (m.user?.name === myRole ? { ...m, _clientRead: true } : m))
        );
      }
    };

    const onMessagesRead = ({ consultationId: cid, role, ids }) => {
      if (cid !== consultationId) return;
      if (role === "doctor" && Array.isArray(ids) && ids.length) {
        const idSet = new Set(ids.map(String));
        setMessages((prev) =>
          prev.map((m) =>
            m.user?.name === myRole && idSet.has(String(m._id))
              ? { ...m, _clientRead: true }
              : m
          )
        );
      }
    };

    const onChatClosed = () => setJoined(false);
    const onErr = (e) => console.error("chatError:", e?.message || e);

    s.on("connect", onConnect);
    s.on("participants", onParticipants);
    s.on("joinedConsultation", onJoined);
    s.on("history", onHistory);
    s.on("message", onMessage);
    s.on("unreadUpdated", onUnreadUpdated);
    s.on("messagesRead", onMessagesRead);
    s.on("chatClosed", onChatClosed);
    s.on("chatError", onErr);

    if (!s.connected) s.connect();
    else bootstrap();

    return () => {
      try {
        s.emit("leaveConsultation", { consultationId });
      } catch {}
      s.off("connect", onConnect);
      s.off("participants", onParticipants);
      s.off("joinedConsultation", onJoined);
      s.off("history", onHistory);
      s.off("message", onMessage);
      s.off("unreadUpdated", onUnreadUpdated);
      s.off("messagesRead", onMessagesRead);
      s.off("chatClosed", onChatClosed);
      s.off("chatError", onErr);
      if (readTimerRef.current) clearTimeout(readTimerRef.current);
      s.disconnect();
    };
  }, [consultationId, payload, loggedInUser, hospitalProfileId]);

  // ---- SEND ----
  const send = () => {
    const value = (text || "").trim();
    if (!value || !joined) return;
    socket.current.emit("sendMessage", { consultationId, text: value });
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Utility to format date labels
  const formatDateLabel = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isToday = d.toDateString() === today.toDateString();
    const isYesterday = d.toDateString() === yesterday.toDateString();

    if (isToday) return "Today";
    if (isYesterday) return "Yesterday";

    return d.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((acc, msg) => {
    const label = formatDateLabel(msg.createdAt);
    if (!acc[label]) acc[label] = [];
    acc[label].push(msg);
    return acc;
  }, {});

  console.log('Doctor profile :', participants);

  const doctorName = participants?.doctor?.displayName || data?.DoctorName || "Doctor";
  const doctorAvatar = participants?.doctor?.avatar || data?.profilePic || null;

  return (
    <section className="chat-overlay" onClick={closeFunction}>
      <div className="chat-container" onClick={(e) => e.stopPropagation()}>
        <header className="chat-header">
          <FiChevronLeft onClick={closeFunction} className="back-icon" />
          {/* <div className="chat-title">Consultation Chat</div> */}
          <div className="peer">
            {doctorAvatar ? (
              <img className="avatar-chat" src={`${process.env.REACT_APP_API_URL}/${doctorAvatar}`} alt="doctor" />
            ) : (
              <div className="avatar-chat">{initials(doctorName)}</div>
            )}
            <span className="peer-name">{doctorName}</span>
          </div>
        </header>

        {joined ? (
          <>
            <div ref={listRef} className="chat-messages" style={{display: "none"}}>
              {messages.map((m) => {
                const isMe = m.user?.name === myRole;
                // const avatarUrl = isMe ? participants?.hospital?.avatar || null : doctorAvatar;
                // const fallbackInitials = isMe
                //   ? initials(participants?.hospital?.displayName || "Me")
                //   : initials(doctorName);

                return (
                  <div key={m._id} className={`row ${isMe ? "me" : "other"}`}>
                    <div className="bubble">
                      <div>{m.text}</div>
                      <div className="timeTicks">
                        <span>
                          {new Date(m.createdAt).toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </span>
                        {isMe && (
                          
                          <span className="tick">
                            {m._clientRead ? (
                             <IoCheckmarkDoneOutline color="#25D366" size={18} />
                            ) : (
                              <IoCheckmarkOutline color="#7c7f86ff" size={18} />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div ref={listRef} className="chat-messages">
              {Object.entries(groupedMessages).map(([dateLabel, list]) => (
                <div key={dateLabel}>
                  
                  <div className='date-badge-container'>
                    <div className="date-badge">{dateLabel}</div>
                  </div>

                  {list.map((m) => {
                    const isMe = m.user?.name === myRole;
                    return (
                      <div key={m._id} className={`row ${isMe ? "me" : "other"}`}>
                        <div className="bubble">
                          <div>{m.text}</div>
                          <div className="timeTicks">
                            <span>
                              {new Date(m.createdAt).toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </span>
                            {isMe && (
                              <span className="tick">
                                {m._clientRead ? (
                                  <IoCheckmarkDoneOutline color="#25D366" size={18} />
                                ) : (
                                  <IoCheckmarkOutline color="#7c7f86ff" size={18} />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="chat-input">
              <textarea
                rows={1}
                placeholder={"Type a message…"}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button onClick={send} className="send-btn" disabled={!joined || !text.trim()}>
                <IoIosSend size={20} />
                <span className="send-label">Send</span>
              </button>
            </div>
          </>
        ) : (
          <div className="loading-wrap">
            <Loader />
          </div>
        )}
      </div>
    </section>
  );
}
